import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Analytics as AnalyticsIcon,
  Web as WebIcon,
  Computer as ComputerIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import { stockService } from '../../services/stockService';
import { toast } from 'react-toastify';

interface BackgroundJobsModalProps {
  open: boolean;
  onClose: () => void;
}

interface TaskInfo {
  task_id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  started_at?: string;
  completed_at?: string;
  result?: any;
  error?: string;
  progress?: number;
  progress_message?: string;
  total_items?: number;
  processed_items?: number;
  chrome_processes?: number;
  scraping_window?: any;
  scraping_stats?: any;
}

interface TasksResponse {
  tasks: { [key: string]: TaskInfo };
  total_tasks: number;
  active_tasks: number;
  chrome_processes: number;
  scraping_allowed: boolean;
  timestamp: string;
}

const BackgroundJobsModal: React.FC<BackgroundJobsModalProps> = ({ open, onClose }) => {
  const [tasks, setTasks] = useState<TasksResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await stockService.getTaskStatus();
      setTasks(response);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to fetch background job status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh every 3 seconds when modal is open and auto-refresh is enabled
  useEffect(() => {
    if (open) {
      fetchTasks();
      
      if (autoRefresh) {
        const interval = setInterval(fetchTasks, 3000);
        return () => clearInterval(interval);
      }
    }
  }, [open, autoRefresh, fetchTasks]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running': return <PlayArrowIcon fontSize="small" />;
      case 'completed': return <CheckCircleIcon fontSize="small" />;
      case 'failed': return <ErrorIcon fontSize="small" />;
      case 'pending': return <PauseIcon fontSize="small" />;
      default: return undefined;
    }
  };

  const getTaskIcon = (taskName: string) => {
    if (taskName.toLowerCase().includes('price')) return <ScheduleIcon />;
    if (taskName.toLowerCase().includes('analysis')) return <AnalyticsIcon />;
    if (taskName.toLowerCase().includes('scrap')) return <WebIcon />;
    return <ComputerIcon />;
  };

  const formatDuration = (startTime?: string, endTime?: string) => {
    if (!startTime) return 'N/A';
    
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    
    if (diffMins > 0) {
      return `${diffMins}m ${diffSecs % 60}s`;
    }
    return `${diffSecs}s`;
  };

  const renderTaskCard = (task: TaskInfo) => (
    <Card key={task.task_id} variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            {getTaskIcon(task.name)}
            <Typography variant="h6" component="div">
              {task.name}
            </Typography>
          </Box>
          <Chip
            {...(getStatusIcon(task.status) && { icon: getStatusIcon(task.status) })}
            label={task.status.toUpperCase()}
            color={getStatusColor(task.status) as any}
            size="small"
          />
        </Box>

        {/* Progress Bar */}
        {task.progress !== undefined && task.progress >= 0 && (
          <Box mb={2}>
            <LinearProgress 
              variant="determinate" 
              value={task.progress} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Box display="flex" justifyContent="space-between" mt={0.5}>
              <Typography variant="caption" color="textSecondary">
                {task.progress}% complete
              </Typography>
              {task.processed_items !== undefined && task.total_items !== undefined && (
                <Typography variant="caption" color="textSecondary">
                  {task.processed_items}/{task.total_items} items
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Running task with no progress */}
        {task.status === 'running' && task.progress === undefined && (
          <LinearProgress sx={{ mb: 2, height: 4, borderRadius: 2 }} />
        )}

        {/* Progress Message */}
        {task.progress_message && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {task.progress_message}
          </Typography>
        )}

        {/* Scraping-specific info */}
        {task.task_id === 'scraping_session' && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Chrome Processes
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {task.chrome_processes || 0}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">
                  Scraping Window
                </Typography>
                <Typography variant="body2" fontWeight="bold" color={task.scraping_window?.allowed ? 'success.main' : 'error.main'}>
                  {task.scraping_window?.allowed ? 'Active' : 'Inactive'}
                </Typography>
              </Grid>
            </Grid>
            
            {task.scraping_stats && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  Stats: {task.scraping_stats.scraped_stocks || 0} scraped, {task.scraping_stats.failed_stocks || 0} failed, {task.scraping_stats.skipped_stocks || 0} skipped
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Timing Info */}
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Typography variant="caption" color="textSecondary">
            {task.started_at && `Started: ${new Date(task.started_at).toLocaleTimeString()}`}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Duration: {formatDuration(task.started_at, task.completed_at)}
          </Typography>
        </Box>

        {/* Error Message */}
        {task.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {task.error}
          </Alert>
        )}

        {/* Result Summary */}
        {task.status === 'completed' && task.result && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="textSecondary">
              Result: {JSON.stringify(task.result).substring(0, 100)}...
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Background Jobs Status</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={autoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}>
              <IconButton
                onClick={() => setAutoRefresh(!autoRefresh)}
                color={autoRefresh ? "primary" : "default"}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && !tasks && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {tasks && (
          <Box>
            {/* Summary */}
            <Box mb={3}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" color="primary">
                        {tasks.total_tasks}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Total Jobs
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" color="warning.main">
                        {tasks.active_tasks}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Active Jobs
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="h4" color="info.main">
                        {tasks.chrome_processes}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Chrome Processes
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography 
                        variant="h6" 
                        color={tasks.scraping_allowed ? "success.main" : "error.main"}
                      >
                        {tasks.scraping_allowed ? "OPEN" : "CLOSED"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Scraping Window
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Task Cards */}
            {Object.keys(tasks.tasks).length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography color="textSecondary">
                  No background jobs currently running or recently completed
                </Typography>
              </Box>
            ) : (
              Object.values(tasks.tasks).map(renderTaskCard)
            )}

            {/* Last Updated */}
            <Box textAlign="center" mt={2}>
              <Typography variant="caption" color="textSecondary">
                Last updated: {new Date(tasks.timestamp).toLocaleTimeString()}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={fetchTasks} disabled={loading} startIcon={<RefreshIcon />}>
          Refresh Now
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BackgroundJobsModal; 